import { extractArrayFromResponse } from '@/lib/api/utils/responseParser'
import type { CompanyOwnerRow } from '../types'

/**
 * Normalize API payloads to match Angular's `this.ownersList = response` (plain array of rows).
 * Handles Axios `data`, nested Laravel-style wrappers, numeric-key objects, and alternate field names.
 */
export function parseCompanyOwnersApiResponse(raw: unknown): CompanyOwnerRow[] {
  if (raw == null) return []

  if (Array.isArray(raw)) {
    return raw.map(normalizeOwnerRow).filter((r): r is CompanyOwnerRow => r != null && String(r.company_owner).trim() !== '')
  }

  if (typeof raw !== 'object') return []

  const o = raw as Record<string, unknown>

  const tryArray = (v: unknown): CompanyOwnerRow[] => {
    if (!Array.isArray(v)) return []
    return v.map(normalizeOwnerRow).filter((r): r is CompanyOwnerRow => r != null && String(r.company_owner).trim() !== '')
  }

  let list = tryArray(extractArrayFromResponse<unknown>(raw))
  if (list.length) return list

  if (Array.isArray(o.company_owners)) return tryArray(o.company_owners)

  // { data: [...] } already tried by extractArrayFromResponse; try nested { data: { data: [...] } }
  if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
    const inner = o.data as Record<string, unknown>
    list = tryArray(inner.data ?? inner.company_owners ?? inner.owners)
    if (list.length) return list
  }

  // Object with only numeric keys (e.g. JSON decoded PHP associative as object)
  const keys = Object.keys(o)
  if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
    return keys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => normalizeOwnerRow(o[k]))
      .filter((r): r is CompanyOwnerRow => r != null && String(r.company_owner).trim() !== '')
  }

  const single = normalizeOwnerRow(o)
  return single && String(single.company_owner).trim() !== '' ? [single] : []
}

function normalizeOwnerRow(row: unknown): CompanyOwnerRow | null {
  if (row == null) return null
  if (typeof row === 'string') {
    const s = row.trim()
    return s ? { owner_id: null, company_owner: s } : null
  }
  if (typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const name =
    pickString(r.company_owner) ??
    pickString(r.owner_name) ??
    pickString(r.name) ??
    (typeof r.owner === 'string' ? pickString(r.owner) : undefined) ??
    ''
  const rawId = r.owner_id ?? r.id ?? r.fk_company_owner_id ?? r.fk_owner_id
  const ownerId = normalizeOwnerId(rawId)
  if (!name.trim() && ownerId == null) return null
  return {
    owner_id: ownerId,
    company_owner: name,
  }
}

function pickString(v: unknown): string | undefined {
  if (v == null) return undefined
  const s = String(v).trim()
  return s === '' ? undefined : s
}

function normalizeOwnerId(v: unknown): string | number | null {
  if (v == null) return null
  if (typeof v === 'string' || typeof v === 'number') return v
  return null
}
